import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { ColorPicker } from "primereact/colorpicker";
import { Dropdown } from "primereact/dropdown";
import { useThemeContext } from "../../../../contexts/ThemeContext/ThemeContext";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import ThemeService from "../../../../service/ThemeService";
import ProductService from "../../../../service/ProductService";
import { resolveThemeImageUrl } from "../../../../utilities/resolveThemeImageUrl";
import localeText from "../../../../locale";
import { THEME_DEFAULTS } from "../../../../constants/themeDefaults";
import "./WhiteLabelPage.css";
import { InputText } from "primereact/inputtext";

const FONT_OPTIONS = [
  { label: "Inter", value: "Inter" },
  { label: "Plus Jakarta Sans", value: "Plus Jakarta Sans" },
  { label: "IBM Plex Sans", value: "IBM Plex Sans" },
  { label: "DM Sans", value: "DM Sans" },
  { label: "Manrope", value: "Manrope" },
  { label: "Public Sans", value: "Public Sans" },
  { label: "Fira Sans", value: "Fira Sans" },
  { label: "Work Sans", value: "Work Sans" },
  { label: "Albert Sans", value: "Albert Sans" },
];

const themeService = new ThemeService();
const productService = new ProductService();

const uploadOptions = { className: "hidden" };

const buildFileUploadEmptyTemplate = (uploadRef, formatKey) => (
  <div className="whitelabel-fileupload-empty">
    <button
      type="button"
      className="whitelabel-fileupload-icon-btn"
      onClick={() => {
        const input = uploadRef.current?.getInput?.();
        if (input) input.click();
      }}
      aria-label={localeText("whiteLabelChooseFile")}
    >
      <i className="pi pi-cloud-upload" />
    </button>
    <p className="whitelabel-fileupload-primary">
      <span className="whitelabel-upload-highlight">{localeText("whiteLabelClickToUpload")}</span>
      <span className="whitelabel-upload-muted"> {localeText("whiteLabelOrDragDrop")}</span>
    </p>
    <p className="whitelabel-fileupload-secondary">{localeText(formatKey)}</p>
  </div>
);

const WhiteLabelPage = () => {
  const history = useHistory();
  const { isSubnetLeader, theme, refreshTheme, isLoading } = useThemeContext();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const logoUploadRef = useRef(null);
  const faviconUploadRef = useRef(null);

  const [themeName, setThemeName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState(THEME_DEFAULTS.primary);
  const [secondaryColor, setSecondaryColor] = useState(THEME_DEFAULTS.secondary);
  const [backgroundColor, setBackgroundColor] = useState(THEME_DEFAULTS.backgroundPage);
  const [textColor, setTextColor] = useState(THEME_DEFAULTS.textBody);
  const [fontFamily, setFontFamily] = useState(THEME_DEFAULTS.fontFamily);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isLoading && isSubnetLeader === false) {
      history.replace("/dashboard");
    }
  }, [isLoading, isSubnetLeader, history]);

  useEffect(() => {
    if (theme) {
      const payload = theme.theme ?? theme;
      setThemeName(theme.themeName || "");
      setLogoUrl(payload?.assets?.logoUrl || "");
      setFaviconUrl(payload?.assets?.faviconUrl || "");
      setPrimaryColor(payload?.colors?.primary?.main || THEME_DEFAULTS.primary);
      setSecondaryColor(payload?.colors?.secondary || THEME_DEFAULTS.secondary);
      setBackgroundColor(payload?.colors?.background?.page || THEME_DEFAULTS.backgroundPage);
      setTextColor(payload?.colors?.text?.body || THEME_DEFAULTS.textBody);
      setFontFamily(payload?.typography?.fontFamilyName || THEME_DEFAULTS.fontFamily);
    } else if (isSubnetLeader) {
      setThemeName("");
      setLogoUrl("");
      setFaviconUrl("");
      setPrimaryColor(THEME_DEFAULTS.primary);
      setSecondaryColor(THEME_DEFAULTS.secondary);
      setBackgroundColor(THEME_DEFAULTS.backgroundPage);
      setTextColor(THEME_DEFAULTS.textBody);
      setFontFamily(THEME_DEFAULTS.fontFamily);
    }
  }, [theme, isSubnetLeader]);

  const uploadImage = async (file, setUrl) => {
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) return;
    try {
      const res = await productService.postUploadImage(token, file);
      if (res?.messageCode === 200000 && res?.result) {
        setUrl(res.result);
        showSuccessToast({ detail: "whiteLabelImageUploaded" });
      } else {
        showErrorToast({ code: res?.messageCode || "uploadError" });
      }
    } catch (err) {
      showErrorToast({ detail: "unexpectedErrorUploadImg" });
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.files?.[0];
    if (!file) return;
    if (file.size / 1024 / 1024 > 5) {
      showErrorToast({ detail: "fileSizeTooBig" });
      if (logoUploadRef.current) logoUploadRef.current.clear();
      return;
    }
    uploadImage(file, setLogoUrl);
    if (logoUploadRef.current) logoUploadRef.current.clear();
  };

  const handleFaviconSelect = (e) => {
    const file = e.files?.[0];
    if (!file) return;
    if (file.size / 1024 / 1024 > 2) {
      showErrorToast({ detail: "fileSizeTooBig" });
      if (faviconUploadRef.current) faviconUploadRef.current.clear();
      return;
    }
    uploadImage(file, setFaviconUrl);
    if (faviconUploadRef.current) faviconUploadRef.current.clear();
  };

  const toHex = (v) => (v && !v.startsWith("#") ? `#${v}` : v);

  const buildPayload = () => ({
    theme: {
      assets: {
        // O salvamento substitui o tema inteiro: mantém as outras logos (celular e Portale da Federnet)
        ...(theme?.theme?.assets ?? theme?.assets ?? {}),
        logoUrl: logoUrl || undefined,
        faviconUrl: faviconUrl || undefined,
      },
      colors: {
        background: { page: toHex(backgroundColor) },
        primary: { main: toHex(primaryColor) },
        secondary: toHex(secondaryColor),
        text: { body: toHex(textColor) },
      },
      typography: { fontFamilyName: fontFamily },
    },
    themeName: themeName || localeText("whiteLabelCustomNetworkDefault"),
  });

  const handleSave = async () => {
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) return;
    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (theme?.id) {
        const res = await themeService.updateThemeByUser(token, payload);
        if (res?.messageCode === 200000) {
          await refreshTheme();
          showSuccessToast({ detail: "whiteLabelThemeUpdated" });
        } else {
          showErrorToast({ code: res?.messageCode });
        }
      } else {
        const res = await themeService.createTheme(token, payload);
        if (res?.messageCode === 200000) {
          await refreshTheme();
          showSuccessToast({ detail: "whiteLabelThemeCreated" });
        } else {
          showErrorToast({ code: res?.messageCode });
        }
      }
    } catch (err) {
      showErrorToast({ detail: "unexpectedSystemBehaviour" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetChanges = () => {
    setLogoUrl(THEME_DEFAULTS.logoUrl);
    setFaviconUrl(THEME_DEFAULTS.faviconUrl);
    setPrimaryColor(THEME_DEFAULTS.primary);
    setSecondaryColor(THEME_DEFAULTS.secondary);
    setBackgroundColor(THEME_DEFAULTS.backgroundPage);
    setTextColor(THEME_DEFAULTS.textBody);
    setFontFamily(THEME_DEFAULTS.fontFamily);
    showSuccessToast({ detail: "whiteLabelChangesReset" });
  };

  if (isLoading) {
    return (
      <div className="white-label-page flex justify-content-center align-items-center min-h-20rem">
        <i className="pi pi-spin pi-spinner text-4xl text-primary"></i>
      </div>
    );
  }
  if (isSubnetLeader === false) return null;

  return (
    <div className="white-label-page manager-dashboard-theme">
      <div className="white-label-header">
        <div>
          <h1 className="white-label-title">{localeText("whiteLabelSettingsTitle")}</h1>
          <p className="white-label-subtitle">{localeText("whiteLabelSettingsSubtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            label={localeText("whiteLabelDefaultSettings")}
            icon="pi pi-refresh"
            style={{
              backgroundColor: "var(--theme-background, #1DAF61)",
              color: "var(--theme-text, #171717)",
              border: "1px solid var(--theme-border, #171717)",
            }}
            className="border-round-md p-no-outline"
            onClick={handleResetChanges}
          />
          <Button
            label={localeText("whiteLabelSaveChanges")}
            style={{ backgroundColor: "var(--theme-primary, #1DAF61)", color: "#fff" }}
            className="border-round-md border-none p-no-outline"
            loading={isSaving}
            onClick={handleSave}
          />
        </div>
      </div>

      <div className="white-label-content">
        <div className="white-label-section">
          <div
            className="px-3 py-2 white-label-section-title border-round-sm"
            style={{ backgroundColor: "var(--theme-border, #F7F7F7)" }}
          >
            <div className="">{localeText("whiteLabelAssets")}</div>
          </div>

          <div className="flex flex-column gap-3">
            <div className="white-label-field">
              <label>{localeText("whiteLabelLogo")}</label>
              <div className="white-label-asset-row">
                {logoUrl && (
                  <div className="white-label-preview">
                    <img src={resolveThemeImageUrl(logoUrl) || logoUrl} alt={localeText("whiteLabelLogoPreview")} />
                  </div>
                )}
                <FileUpload
                  ref={logoUploadRef}
                  className="whitelabel-fileupload-prototype"
                  uploadOptions={uploadOptions}
                  chooseOptions={{ className: "hidden" }}
                  chooseLabel=""
                  emptyTemplate={buildFileUploadEmptyTemplate(
                    logoUploadRef,
                    "whiteLabelFileFormatsLogo",
                  )}
                  onSelect={handleLogoSelect}
                  accept="image/*"
                  customUpload
                />
              </div>
            </div>
            <div className="white-label-field">
              <label>{localeText("whiteLabelFavicon")}</label>
              <div className="white-label-asset-row">
                {faviconUrl && (
                  <div className="white-label-preview favicon-preview">
                    <img
                      src={resolveThemeImageUrl(faviconUrl) || faviconUrl}
                      alt={localeText("whiteLabelFaviconPreview")}
                    />
                  </div>
                )}
                <FileUpload
                  ref={faviconUploadRef}
                  className="whitelabel-fileupload-prototype"
                  uploadOptions={uploadOptions}
                  chooseOptions={{ className: "hidden" }}
                  chooseLabel=""
                  emptyTemplate={buildFileUploadEmptyTemplate(
                    faviconUploadRef,
                    "whiteLabelFileFormatsFavicon",
                  )}
                  onSelect={handleFaviconSelect}
                  accept="image/*"
                  customUpload
                />
              </div>
            </div>
          </div>
        </div>

        <div className="white-label-section">
          <div
            className="px-3 py-2 white-label-section-title border-round-sm"
            style={{ backgroundColor: "var(--theme-border, #F7F7F7)" }}
          >
            <div className="">{localeText("whiteLabelBrandColors")}</div>
          </div>
          <div className="flex flex-column gap-3">
            <div className="white-label-field flex justify-content-between align-items-center">
              <div>
                <div
                  className="font-medium mb-1 text-sm"
                  style={{ color: "var(--theme-text, #171717)" }}
                >
                  {localeText("whiteLabelPrimaryColor")}
                </div>
                <div className="mb-0 text-xs" style={{ color: "var(--theme-text-muted, #666666)" }}>
                  {localeText("whiteLabelPrimaryColorHelp")}
                </div>
              </div>

              <div className="white-label-color-row">
                <ColorPicker
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.value)}
                  format="hex"
                  className="border-round-lg"
                />
                <InputText
                  className="border-round-lg"
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  value={primaryColor}
                />
              </div>
            </div>
            <div className="white-label-field flex justify-content-between align-items-center">
              <div>
                <div
                  className="font-medium mb-1 text-sm"
                  style={{ color: "var(--theme-text, #171717)" }}
                >
                  {localeText("whiteLabelSecondaryColor")}
                </div>
                <div className="mb-0 text-xs" style={{ color: "var(--theme-text-muted, #666666)" }}>
                  {localeText("whiteLabelSecondaryColorHelp")}
                </div>
              </div>
              <div className="white-label-color-row">
                <ColorPicker
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.value)}
                  format="hex"
                />
                <InputText
                  className="border-round-lg"
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  value={secondaryColor}
                />
              </div>
            </div>
            <div className="white-label-field flex justify-content-between align-items-center">
              <div>
                <div
                  className="font-medium mb-1 text-sm"
                  style={{ color: "var(--theme-text, #171717)" }}
                >
                  {localeText("whiteLabelBackgroundColor")}
                </div>
                <div className="mb-0 text-xs" style={{ color: "var(--theme-text-muted, #666666)" }}>
                  {localeText("whiteLabelBackgroundColorHelp")}
                </div>
              </div>
              <div className="white-label-color-row">
                <ColorPicker
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.value)}
                  format="hex"
                />
                <InputText
                  className="border-round-lg"
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  value={backgroundColor}
                />
              </div>
            </div>
            <div className="white-label-field flex justify-content-between align-items-center">
              <div>
                <div
                  className="font-medium mb-1 text-sm"
                  style={{ color: "var(--theme-text, #171717)" }}
                >
                  {localeText("whiteLabelTextColor")}
                </div>
                <div className="mb-0 text-xs" style={{ color: "var(--theme-text-muted, #666666)" }}>
                  {localeText("whiteLabelTextColorHelp")}
                </div>
              </div>
              <div className="white-label-color-row">
                <ColorPicker
                  value={textColor}
                  onChange={(e) => setTextColor(e.value)}
                  format="hex"
                />
                <InputText
                  className="border-round-lg"
                  onChange={(e) => setTextColor(e.target.value)}
                  value={textColor}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="white-label-section">
          <div
            className="px-3 py-2 white-label-section-title border-round-sm"
            style={{ backgroundColor: "var(--theme-border, #F7F7F7)" }}
          >
            <div className="">{localeText("whiteLabelTypography")}</div>
          </div>
          <div className="white-label-field flex justify-content-between align-items-center">
            <div>
              <div
                className="font-medium mb-1 text-sm"
                style={{ color: "var(--theme-text, #171717)" }}
              >
                {localeText("whiteLabelFont")}
              </div>
              <div className="mb-0 text-xs" style={{ color: "var(--theme-text-muted, #666666)" }}>
                {localeText("whiteLabelFontHelp")}
              </div>
            </div>
            <div className="white-label-color-row">
              <Dropdown
                value={fontFamily}
                options={FONT_OPTIONS}
                onChange={(e) => setFontFamily(e.value)}
                placeholder={localeText("whiteLabelSelectFont")}
                className="border-round-lg p-no-outline"
                style={{ minWidth: "200px" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhiteLabelPage;
